// src/components/Prospecting/TradeShows/TradeShowsTable.js

import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Typography,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";

import BrandPagination from "../../../layouts/Pagination";
import {
  normalizeTradeShowCountryCode,
  normalizeTradeShowUrl,
} from "./TradeShowsHelpers";
import { COUNTRY_MAP } from "../../../utils/Countries";
import { CANADA_PROVINCE_MAP, US_STATE_MAP } from "../../../utils/States";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const TRADE_SHOW_COLUMN_WIDTH = 340;
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const HEAD_CELL_SX = {
  backgroundColor: BRAND_BLUE,
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const DATA_CELL_SX = {
  fontSize: "0.875rem",
  color: "#344863",
  borderBottom: "1px solid #edf0f5",
  py: 0.875,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: BRAND_BLUE,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};
const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiInputBase-input": {
    py: 0.75,
    color: "#00336C",
    fontWeight: 700,
  },
  "& input": {
    color: "#00336C !important",
    WebkitTextFillColor: "#00336C",
    caretColor: "#00336C",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};
const FILTER_SECTION_LABEL_SX = {
  px: 2,
  pt: 1.2,
  pb: 0.4,
  color: "#00336C",
  fontSize: "0.76rem",
  fontWeight: 800,
  textTransform: "uppercase",
};
const MONTHS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

/* =========================
   HELPERS (move with table)
========================= */

function toTitleCase(str) {
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function countryLabel(option) {
  return COUNTRY_MAP[option] || toTitleCase(option);
}

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString();
}

function formatLocation(row) {
  return [row.city, row.state, row.country].filter(Boolean).join(", ") || "—";
}

function cleanDisplay(value) {
  return value ? String(value).trim() : "";
}

/* =========================
   COMPONENT
========================= */

export default function TradeShowsTable({
  rows = [],
  paginatedRows,
  cleanRows,
  loading,

  // selection
  selectedIds,
  handleToggleOne,
  handleTogglePage,
  allCurrentSelected,
  someCurrentSelected,
  currentPageIds,

  // pagination
  page,
  totalPages,
  setPage,

  selectedCountry = [],
  onCountryChange,
  selectedState = [],
  onStateChange,
  selectedIndustry = [],
  onIndustryChange,
  selectedMonth = [],
  onMonthChange,
  selectedYear = [],
  onYearChange,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const normalizedSearch = filterSearch.trim().toLowerCase();
  const filterOption = useCallback(
    (label) => !normalizedSearch || label.toLowerCase().includes(normalizedSearch),
    [normalizedSearch],
  );

  const countryOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const country = normalizeTradeShowCountryCode(
        cleanDisplay(row.new_country) || cleanDisplay(row.country),
      );
      if (country) set.add(country);
    });
    return [...set].sort();
  }, [rows]);

  const stateOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const rowCountry = normalizeTradeShowCountryCode(
        cleanDisplay(row.new_country) || cleanDisplay(row.country),
      );
      if (!selectedCountry.includes(rowCountry)) return;

      const rawState = cleanDisplay(row.state);
      if (!rawState) return;

      const code = rawState.toUpperCase();
      const isUS = Boolean(US_STATE_MAP[code]);
      const isCA = Boolean(CANADA_PROVINCE_MAP[code]);
      if (!isUS && !isCA) return;

      map.set(code, {
        code,
        label: US_STATE_MAP[code] || CANADA_PROVINCE_MAP[code] || rawState,
        group: isUS ? "United States" : "Canada",
      });
    });

    return [...map.values()].sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      return a.label.localeCompare(b.label);
    });
  }, [rows, selectedCountry]);

  const industryOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const industry = cleanDisplay(row.new_industry) || cleanDisplay(row.industry);
      if (industry) set.add(industry);
    });
    return [...set].sort();
  }, [rows]);

  const yearOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const raw = row.start_date_raw;
      if (!raw) return;
      const date = new Date(raw);
      if (!Number.isNaN(date.getTime())) set.add(String(date.getFullYear()));
    });
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [rows]);

  const filteredCountryOptions = useMemo(
    () =>
      countryOptions.filter((option) =>
        filterOption(countryLabel(option)),
      ),
    [countryOptions, filterOption],
  );
  const filteredStateOptions = useMemo(
    () =>
      stateOptions.filter((option) =>
        filterOption(`${option.label} ${option.group}`),
      ),
    [filterOption, stateOptions],
  );
  const filteredIndustryOptions = useMemo(
    () => industryOptions.filter((option) => filterOption(option)),
    [filterOption, industryOptions],
  );
  const filteredMonthOptions = useMemo(
    () => MONTHS.filter((option) => filterOption(option.label)),
    [filterOption],
  );
  const filteredYearOptions = useMemo(
    () => yearOptions.filter((option) => filterOption(option)),
    [filterOption, yearOptions],
  );

  const openFilterMenu = useCallback((event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  }, []);

  const closeFilterMenu = useCallback(() => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  }, []);

  const toggleStringValue = useCallback((values, value) => {
    return values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value];
  }, []);

  const renderFilterTrigger = (label, filterKey, active = false, minWidth = 108) => (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <Button
        variant="text"
        onClick={(event) => openFilterMenu(event, filterKey)}
        sx={{
          ...HEADER_TRIGGER_SX,
          minWidth,
          color: active ? BRAND_GREEN : BRAND_BLUE,
          border: active ? `1.5px solid ${BRAND_GREEN}` : HEADER_TRIGGER_SX.border,
          backgroundColor: "#ffffff",
          "&:hover": { backgroundColor: "#ffffff", borderColor: active ? BRAND_GREEN : "rgba(255,255,255,0.8)" },
        }}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
      >
        {label}
      </Button>
    </Stack>
  );

  const renderSearchField = (placeholder) => (
    <Box sx={SEARCH_FIELD_SX}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={filterSearch}
        onChange={(event) => setFilterSearch(event.target.value)}
        onKeyDown={(event) => event.stopPropagation()}
        placeholder={placeholder}
        sx={FILTER_SEARCH_INPUT_SX}
        slotProps={{
          input: {
            startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
          },
        }}
      />
    </Box>
  );

  const renderOptionItem = ({ key, label, checked, onClick, disabled = false }) => (
    <MenuItem key={key} selected={checked} disabled={disabled} onClick={onClick}>
      {label}
    </MenuItem>
  );

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
        },
      },
    };

    if (activeFilter === "dates") {
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search month or year")}
          <MenuItem
            selected={!selectedMonth.length && !selectedYear.length}
            onClick={() => {
              onMonthChange([]);
              onYearChange([]);
            }}
          >
            All Dates
          </MenuItem>
          <Typography sx={FILTER_SECTION_LABEL_SX}>Month</Typography>
          {filteredMonthOptions.map((option) =>
            renderOptionItem({
              key: `month-${option.value}`,
              label: option.label,
              checked: selectedMonth.includes(option.value),
              onClick: () =>
                onMonthChange(toggleStringValue(selectedMonth, option.value)),
            }),
          )}
          <Typography sx={FILTER_SECTION_LABEL_SX}>Year</Typography>
          {filteredYearOptions.map((option) =>
            renderOptionItem({
              key: `year-${option}`,
              label: option,
              checked: selectedYear.includes(option),
              onClick: () => onYearChange(toggleStringValue(selectedYear, option)),
            }),
          )}
        </Menu>
      );
    }

    if (activeFilter === "location") {
      const northAmericaSelected =
        selectedCountry.includes("US") || selectedCountry.includes("CA");

      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search country or state/province")}
          <MenuItem
            selected={!selectedCountry.length && !selectedState.length}
            onClick={() => {
              onCountryChange([]);
              onStateChange([]);
            }}
          >
            All Locations
          </MenuItem>
          <Typography sx={FILTER_SECTION_LABEL_SX}>Country</Typography>
          {filteredCountryOptions.map((option) =>
            renderOptionItem({
              key: `country-${option}`,
              label: countryLabel(option),
              checked: selectedCountry.includes(option),
              onClick: () => {
                const next = toggleStringValue(selectedCountry, option);
                onCountryChange(next);
                if (!next.includes("US") && !next.includes("CA")) {
                  onStateChange([]);
                }
              },
            }),
          )}
          {northAmericaSelected && (
            <>
              <Typography sx={FILTER_SECTION_LABEL_SX}>State / Province</Typography>
              {filteredStateOptions.length ? (
                filteredStateOptions.map((option) =>
                  renderOptionItem({
                    key: `state-${option.code}`,
                    label: `${option.label} (${option.group})`,
                    checked: selectedState.includes(option.code),
                    onClick: () =>
                      onStateChange(toggleStringValue(selectedState, option.code)),
                  }),
                )
              ) : (
                <MenuItem disabled>No states or provinces</MenuItem>
              )}
            </>
          )}
        </Menu>
      );
    }

    if (activeFilter === "industry") {
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search industries")}
          <MenuItem
            selected={!selectedIndustry.length}
            onClick={() => onIndustryChange([])}
          >
            All Industries
          </MenuItem>
          {filteredIndustryOptions.map((option) =>
            renderOptionItem({
              key: option,
              label: option,
              checked: selectedIndustry.includes(option),
              onClick: () =>
                onIndustryChange(toggleStringValue(selectedIndustry, option)),
            }),
          )}
        </Menu>
      );
    }

    return null;
  };

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          border: `1.5px solid ${BRAND_BLUE}`,
          borderRadius: 2,
          boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: { xs: 980, md: 1100 }, borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              {/* SELECT ALL */}
              <TableCell
                padding="checkbox"
                align="center"
                sx={HEAD_CELL_SX}
              >
                <Checkbox
                  indeterminate={someCurrentSelected && !allCurrentSelected}
                  checked={currentPageIds.length > 0 && allCurrentSelected}
                  onChange={handleTogglePage}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  indeterminateIcon={<RemoveCircleIcon />}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                  }}
                />
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  ...HEAD_CELL_SX,
                  width: TRADE_SHOW_COLUMN_WIDTH,
                  maxWidth: TRADE_SHOW_COLUMN_WIDTH,
                }}
              >
                Trade Show
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>
                {renderFilterTrigger(
                  "Dates",
                  "dates",
                  selectedMonth.length > 0 || selectedYear.length > 0,
                  116,
                )}
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>
                {renderFilterTrigger(
                  "Location",
                  "location",
                  selectedCountry.length > 0 || selectedState.length > 0,
                  132,
                )}
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>
                {renderFilterTrigger(
                  "Industry",
                  "industry",
                  selectedIndustry.length > 0,
                  128,
                )}
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>
                Attendance
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{ background: "white" }}>
            {paginatedRows.map((r, idx) => {
              const id = r.id ?? idx;
              const safeUrl = normalizeTradeShowUrl(r.website);
              const checked = r.id != null && selectedIds.has(r.id);

              return (
                <TableRow
                  key={id}
                  sx={{
                    backgroundColor: "#ffffff",
                    "&:hover": { backgroundColor: "#eef2f7" },
                  }}
                >
                  {/* CHECKBOX */}
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      disabled={r.id == null}
                      checked={checked}
                      onChange={() => handleToggleOne(r.id)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>

                  {/* NAME */}
                  <TableCell
                    align="center"
                    sx={{
                      ...DATA_CELL_SX,
                      width: TRADE_SHOW_COLUMN_WIDTH,
                      maxWidth: TRADE_SHOW_COLUMN_WIDTH,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <Tooltip title={r.name || ""}>
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            maxWidth: TRADE_SHOW_COLUMN_WIDTH,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {safeUrl ? (
                            <Typography
                              component="a"
                              href={safeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontWeight: 700,
                                color: BRAND_BLUE,
                                textDecoration: "none",
                                lineHeight: 1.3,
                                "&:hover": {
                                  textDecoration: "underline",
                                  color: BRAND_GREEN,
                                },
                              }}
                            >
                              {r.name}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontWeight: 700,
                              }}
                            >
                              {r.name}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {/* DATES */}
                  <TableCell align="center" sx={DATA_CELL_SX}>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: BRAND_BLUE,
                      }}
                    >
                      {r.date_range || "—"}
                    </Typography>
                  </TableCell>

                  {/* LOCATION */}
                  <TableCell align="center" sx={{ ...DATA_CELL_SX, color: BRAND_BLUE }}>
                    {formatLocation(r)}
                  </TableCell>

                  {/* INDUSTRY */}
                  <TableCell align="center" sx={DATA_CELL_SX}>
                    {r.industry ? (
                      <Chip
                        label={r.industry}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: BRAND_BLUE,
                          color: "#fff",
                          maxWidth: 220,
                        }}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>

                  {/* ATTENDANCE */}
                  <TableCell align="center" sx={DATA_CELL_SX}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <Chip
                        label={`Attendees: ${formatNumber(r.attendees)}`}
                        size="small"
                        sx={{ bgcolor: "rgba(0,51,108,0.1)", color: BRAND_BLUE, fontWeight: 800, minWidth: 128 }}
                      />
                      <Chip
                        label={`Exhibitors: ${formatNumber(r.exhibitors)}`}
                        size="small"
                        sx={{ bgcolor: "rgba(100,131,21,0.15)", color: BRAND_GREEN, fontWeight: 800, minWidth: 128 }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* EMPTY */}
            {cleanRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography>No results.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {renderFilterMenu()}

      {/* PAGINATION */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 3, px: { xs: 1, sm: 0 } }}>
        <BrandPagination
          page={page}
          count={totalPages}
          onChange={(_e, value) => setPage(value)}
        />
      </Box>
    </>
  );
}
